"use client"

import { useEffect, useState } from 'react';
import CustomerService from '../../api/customers.service';
import { ArrowLeft, Loader2, Edit2, User, Mail, Phone, Building, MapPin, CreditCard, FileText, ExternalLink, BarChart4, MessageSquare, FileIcon, PlusCircle, Calendar, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import FormModal from '@/components/FormModal';
import { EditCustomerForm } from '@/pages/customers/EditCustomerForm';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CollaborationTabs } from '@/components/CollaborationTabs';
import { ActivityForm } from '@/components/crm/ActivityForm';
import CrmActivitiesService from '@/api/crmActivities.service';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { CustomerTaxInfo } from '@/components/tax/CustomerTaxInfo';

interface CustomerDetailsProps {
  publicId: string;
}

export default function CustomerDetail({ publicId }: CustomerDetailsProps) {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isActivityModalOpen, setActivityModalOpen] = useState(false);
  const [customer, setCustomer] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [customerActivities, setCustomerActivities] = useState<any[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const { toast } = useToast();
  
  // Fetch customer activities
  const fetchCustomerActivities = async (customerId: number) => {
    setLoadingActivities(true);
    try {
      const response = await CrmActivitiesService.getCustomerActivities(customerId);
      if (response.data.result === 'success') {
        setCustomerActivities(response.data.activities || []);
      }
    } catch (error) {
      console.error('Error fetching customer activities:', error);
    } finally {
      setLoadingActivities(false);
    }
  };

  // Get icon based on activity type
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'call':
        return <Phone className="h-4 w-4" />;
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'meeting':
        return <Calendar className="h-4 w-4" />;
      case 'note':
        return <FileText className="h-4 w-4" />;
      case 'task':
        return <Check className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  useEffect(() => {
    const fetchCustomer = async () => {
      setIsLoading(true);
      try {
        const response = await CustomerService.getCustomer(publicId);
        console.log("response.data", response.data);
        if (response.data.result === 'invalid') {
          window.location.href = '/logout';
        } else if (response.data.result === 'success') {
          setCustomer(response.data.customer);
          // After getting customer, fetch their activities
          if (response.data.customer?.id) {
            fetchCustomerActivities(response.data.customer.id);
          }
        } else {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load customer details",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    if (publicId) {
      fetchCustomer();
    }
  }, [publicId, toast]);

  // Format date to relative time (e.g., 2 days ago)
  const formatRelativeDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return dateString;
    }
  };

  // Handle activity creation success
  const handleActivityCreated = () => {
    setActivityModalOpen(false);
    // Refresh activities list
    if (customer?.id) {
      fetchCustomerActivities(customer.id);
    }
    toast({
      title: "Success",
      description: "Activity created successfully",
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 dark:bg-slate-900">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading customer details...</p>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-slate-50 dark:bg-slate-900">
        <p className="text-muted-foreground">Customer not found</p>
        <Button onClick={() => window.history.back()} variant="outline" className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="container py-8 mx-auto max-w-7xl">
      <div className="flex flex-col space-y-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => window.history.back()} 
              variant="ghost" 
              size="sm"
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-bold tracking-tight">
              {customer.firstName} {customer.lastName}
            </h1>
            {customer.companyName && (
              <Badge variant="outline" className="ml-2 bg-primary/10 text-primary border-primary/20">
                {customer.companyName}
              </Badge>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button 
              onClick={() => setActivityModalOpen(true)} 
              variant="outline"
              className="gap-2 border-primary/20 text-primary hover:bg-primary/10"
            >
              <PlusCircle className="w-4 h-4" />
              Add Activity
            </Button>
            <Button 
              onClick={() => setModalOpen(true)} 
              variant="outline"
              className="gap-2 border-primary/20 text-primary hover:bg-primary/10"
            >
              <Edit2 className="w-4 h-4" />
              Edit Customer
            </Button>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Column - Customer Profile */}
          <Card className="md:col-span-1 shadow-sm border-slate-200 dark:border-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Customer Profile
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Avatar Placeholder */}
              <div className="flex justify-center mb-6">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center border-4 border-white shadow-md">
                  <User className="w-12 h-12 text-primary/40" />
                </div>
              </div>
              
              {/* Contact Info */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">{customer.emailAddress}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Phone</p>
                    <p className="text-sm text-muted-foreground">{customer.phoneNumber}</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Address</p>
                    <p className="text-sm text-muted-foreground">{customer.street}</p>
                    <p className="text-sm text-muted-foreground">{customer.city}, {customer.state} {customer.zipCode}</p>
                    <p className="text-sm text-muted-foreground">{customer.country}</p>
                  </div>
                </div>
                
                {customer.companyName && (
                  <div className="flex items-start gap-3">
                    <Building className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Company</p>
                      <p className="text-sm text-muted-foreground">{customer.companyName}</p>
                    </div>
                  </div>
                )}
              </div>
              
              <Separator />
              
              {/* Quick Actions */}
              <div className="space-y-2">
                <p className="text-sm font-medium">Quick Actions</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <FileText className="w-4 h-4 mr-2" />
                    Invoices
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Payments
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Portal
                  </Button>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <BarChart4 className="w-4 h-4 mr-2" />
                    Reports
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Right Column - Customer Details */}
          <div className="md:col-span-2 space-y-6">
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid grid-cols-3 w-full md:w-[600px]">
                <TabsTrigger value="details">Customer Details</TabsTrigger>
                <TabsTrigger value="activities">Activities</TabsTrigger>
                <TabsTrigger value="collaboration">
                  <div className="flex items-center gap-1.5">
                    <MessageSquare className="w-4 h-4" />
                    Collaboration
                  </div>
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="mt-4">
                <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                  <CardContent className="pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">First Name</p>
                        <p className="font-medium">{customer.firstName}</p>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Last Name</p>
                        <p className="font-medium">{customer.lastName}</p>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Email Address</p>
                        <p className="font-medium">{customer.emailAddress}</p>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Phone Number</p>
                        <p className="font-medium">{customer.phoneNumber}</p>
                      </div>
                      
                      <div className="space-y-1 md:col-span-2">
                        <p className="text-sm text-muted-foreground">Address</p>
                        <p className="font-medium">{customer.street}, {customer.city}, {customer.state} {customer.zipCode}, {customer.country}</p>
                      </div>
                      
                      {customer.companyName && (
                        <div className="space-y-1 md:col-span-2">
                          <p className="text-sm text-muted-foreground">Company Name</p>
                          <p className="font-medium">{customer.companyName}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Tax Information Section */}
                <div className="mt-6">
                  <CustomerTaxInfo customerId={customer.id} />
                </div>
              </TabsContent>
              
              {/* New Activities Tab */}
              <TabsContent value="activities" className="mt-4">
                <Card className="shadow-sm border-slate-200 dark:border-slate-800">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                      <CardTitle className="text-lg">Customer Activities</CardTitle>
                      <CardDescription>Recent interactions with this customer</CardDescription>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setActivityModalOpen(true)}>
                      <PlusCircle className="h-4 w-4 mr-2" />
                      Add Activity
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {loadingActivities ? (
                      <div className="flex justify-center py-6">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : customerActivities.length > 0 ? (
                      <div className="space-y-4">
                        {customerActivities.map((activity) => (
                          <div key={activity.id} className="flex items-start gap-3 pb-4 border-b border-border/50 last:border-0 last:pb-0">
                            <div className={cn(
                              "h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0",
                              activity.activityType === 'call' ? 'bg-primary/10 text-primary' :
                              activity.activityType === 'email' ? 'bg-green-100 text-green-700' :
                              activity.activityType === 'meeting' ? 'bg-amber-100 text-amber-700' :
                              activity.activityType === 'note' ? 'bg-purple-100 text-purple-700' :
                              'bg-slate-100 text-slate-700'
                            )}>
                              {getActivityIcon(activity.activityType)}
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="text-sm font-medium">{activity.title}</p>
                                  {activity.description && (
                                    <p className="text-xs text-muted-foreground mt-1">{activity.description}</p>
                                  )}
                                </div>
                                <Badge variant="outline" className={cn(
                                  "text-xs",
                                                  activity.status === 'completed' ? 'bg-green-500/10 text-green-700 border-green-200' :
                activity.status === 'canceled' ? 'bg-red-500/10 text-red-700 border-red-200' :
                'bg-primary/10 text-primary border-primary/20'
                                )}>
                                  {activity.status}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground mt-2">
                                {formatRelativeDate(activity.date)} • 
                                {activity.priority && (
                                  <span className={cn(
                                                    activity.priority === 'high' ? 'text-red-600' :
                activity.priority === 'medium' ? 'text-amber-600' :
                'text-primary'
                                  )}> {activity.priority} priority</span>
                                )}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-muted-foreground">No activities recorded yet</p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setActivityModalOpen(true)}
                          className="mt-2"
                        >
                          Add First Activity
                        </Button>
                      </div>
                    )}
                    
                    {customerActivities.length > 0 && (
                      <Button variant="outline" size="sm" className="w-full mt-4" asChild>
                        <a href="/crm/activities">View All Activities</a>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="collaboration" className="mt-4">
                <CollaborationTabs 
                  customerId={customer.id}
                  entityType="customer"
                  entityId={customer.id}
                />
              </TabsContent>
            </Tabs>
            
            {/* Financial Overview Card */}
            <Card className="shadow-sm border-slate-200 dark:border-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart4 className="w-5 h-5 text-primary" />
                  Financial Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-primary/5 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Outstanding Invoices</p>
                    <p className="text-2xl font-bold mt-1">$0.00</p>
                    <p className="text-xs text-muted-foreground mt-2">0 invoices pending</p>
                  </div>
                  
                  <div className="bg-green-50 dark:bg-green-950/20 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Paid This Month</p>
                    <p className="text-2xl font-bold mt-1 text-green-600 dark:text-green-400">$0.00</p>
                    <p className="text-xs text-muted-foreground mt-2">0 payments received</p>
                  </div>
                  
                  <div className="bg-blue-50 dark:bg-blue-950/20 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">Lifetime Value</p>
                    <p className="text-2xl font-bold mt-1 text-blue-600 dark:text-blue-400">$0.00</p>
                    <p className="text-xs text-muted-foreground mt-2">Since {new Date(customer.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      <FormModal
        open={isModalOpen}
        setOpen={setModalOpen}
        title="Update Customer"
      >
        <EditCustomerForm customer={customer} />
      </FormModal>

      <FormModal
        open={isActivityModalOpen}
        setOpen={setActivityModalOpen}
        title="Add Customer Activity"
      >
        <ActivityForm 
          initialCustomerId={customer?.id} 
          onSuccess={handleActivityCreated} 
        />
      </FormModal>
    </div>
  );
} 